import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useCallback, useEffect, useMemo, useState } from "react";
import { foyerApi, type ScreenRecord } from "../http/foyer";
import { tokens } from "../theme";
import type {
  ProvisioningResult,
  ProvisioningSeatSummary,
  ScreenRole,
} from "@shared/types/foyer";
import { roleLabel } from "../utils/devicePresentation";

interface Props {
  open: boolean;
  premiseId: string;
  rooms: { id: string; name?: string }[];
  /** Whether the door-sign role is offered - true only on care premises. Hotels
   *  never see the role picker/column (every pre-seeded screen is a TV). */
  allowDoorSign?: boolean;
  onClose: () => void;
  /** Called after a successful add/import/remove so the parent can refresh. */
  onChanged?: () => void;
}

/** Human-readable copy for a provisioning error reason code. */
function reasonLabel(reason: string): string {
  switch (reason) {
    case "missing_serial":
      return "Missing serial number";
    case "missing_room":
      return "Missing room";
    case "unknown_room":
      return "Room not found in this premise";
    case "room_already_taken":
      return "Room already has a device";
    case "duplicate_serial":
      return "Serial already provisioned here";
    case "serial_in_use_elsewhere":
      return "Serial already in use at another premise";
    case "no_free_licenses":
      return "No free license seats";
    default:
      return reason;
  }
}

export default function ProvisioningDialog({
  open,
  premiseId,
  rooms,
  allowDoorSign = false,
  onClose,
  onChanged,
}: Props) {
  const [screens, setScreens] = useState<ScreenRecord[]>([]);
  const [seats, setSeats] = useState<ProvisioningSeatSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [serial, setSerial] = useState("");
  const [roomId, setRoomId] = useState("");
  const [model, setModel] = useState("");
  const [role, setRole] = useState<ScreenRole>("tv");
  const [adding, setAdding] = useState(false);

  const [importing, setImporting] = useState(false);
  const [lastResult, setLastResult] = useState<ProvisioningResult | null>(null);

  const load = useCallback(async () => {
    if (!premiseId) return;
    try {
      const prov = await foyerApi.provisioningList(premiseId);
      setScreens(prov.screens);
      setSeats(prov.seats);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [premiseId]);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  const takenRoomIds = useMemo(
    () => new Set(screens.filter((s) => s.roomId).map((s) => s.roomId)),
    [screens]
  );
  const freeRooms = useMemo(
    () => rooms.filter((r) => !takenRoomIds.has(r.id)),
    [rooms, takenRoomIds]
  );

  const roomLabel = useCallback(
    (id: string | undefined) => {
      if (!id) return "-";
      const r = rooms.find((x) => x.id === id);
      return r?.name ? r.name : id;
    },
    [rooms]
  );

  const awaiting = useMemo(
    () => screens.filter((s) => s.provisioned),
    [screens]
  );

  const refreshAll = useCallback(async () => {
    await load();
    onChanged?.();
  }, [load, onChanged]);

  const addOne = useCallback(async () => {
    if (!serial.trim() || !roomId) return;
    setAdding(true);
    setError(null);
    try {
      const result = await foyerApi.provisionDevice(premiseId, {
        serial: serial.trim(),
        room: roomId,
        model: model.trim() || undefined,
        role: role === "door-sign" ? "door-sign" : undefined,
      });
      setLastResult(result);
      setSerial("");
      setRoomId("");
      setModel("");
      setRole("tv");
      await refreshAll();
    } catch (err) {
      const code = (err as { code?: string })?.code;
      setError(
        code
          ? reasonLabel(code)
          : err instanceof Error
            ? err.message
            : String(err)
      );
    } finally {
      setAdding(false);
    }
  }, [premiseId, serial, roomId, model, role, refreshAll]);

  const importCsv = useCallback(
    async (file: File | null | undefined) => {
      if (!file) return;
      setImporting(true);
      setError(null);
      try {
        const result = await foyerApi.provisionImport(premiseId, file);
        setLastResult(result);
        await refreshAll();
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setImporting(false);
      }
    },
    [premiseId, refreshAll]
  );

  const removeOne = useCallback(
    async (screenId: string) => {
      if (!window.confirm("Remove this pre-seeded device?")) return;
      try {
        await foyerApi.deleteProvisioned(premiseId, screenId);
        await refreshAll();
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    },
    [premiseId, refreshAll]
  );

  const seatLabel = seats
    ? seats.unlimited
      ? `${seats.activatedCount} provisioned · unlimited seats`
      : `${seats.activatedCount} / ${seats.totalSeats} seats used`
    : "";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h6">Provision devices (zero-touch)</Typography>
            <Typography variant="body2" color="text.secondary">
              Pre-seed devices by serial number. The TV self-activates on first
              boot - no installation code needed.
            </Typography>
          </Box>
          <IconButton onClick={onClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          {error && <Alert severity="error">{error}</Alert>}

          {seats && (
            <Stack
              direction="row"
              spacing={1}
              sx={{ flexWrap: "wrap", gap: 1 }}
            >
              <Chip
                label={`${awaiting.length} awaiting activation`}
                sx={{ bgcolor: tokens.warningTint, color: tokens.warning }}
              />
              <Chip
                label={seatLabel}
                color={
                  !seats.unlimited && !seats.hasFreeSeats ? "error" : "default"
                }
              />
            </Stack>
          )}

          {/* Manual add */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            sx={{ alignItems: { md: "flex-start" } }}
          >
            <TextField
              size="small"
              label="Serial number"
              value={serial}
              onChange={(e) => setSerial(e.target.value)}
              sx={{ minWidth: 180 }}
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="prov-room">Room</InputLabel>
              <Select
                labelId="prov-room"
                label="Room"
                value={roomId}
                onChange={(e) => setRoomId(e.target.value)}
              >
                {freeRooms.length === 0 && (
                  <MenuItem value="" disabled>
                    No free rooms
                  </MenuItem>
                )}
                {freeRooms.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.name ? `${r.name} (${r.id})` : r.id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Model (optional)"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              sx={{ minWidth: 160 }}
            />
            {allowDoorSign && (
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel id="prov-role">Role</InputLabel>
                <Select
                  labelId="prov-role"
                  label="Role"
                  value={role}
                  onChange={(e) => setRole(e.target.value as ScreenRole)}
                >
                  <MenuItem value="tv">TV</MenuItem>
                  <MenuItem value="door-sign">Door sign</MenuItem>
                </Select>
              </FormControl>
            )}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => void addOne()}
              disabled={adding || !serial.trim() || !roomId}
            >
              Add
            </Button>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadFileIcon />}
              disabled={importing}
            >
              {importing ? "Importing…" : "Import CSV"}
              <input
                type="file"
                hidden
                accept=".csv,text/csv,text/plain"
                onChange={(e) => {
                  void importCsv(e.target.files?.[0]);
                  e.target.value = "";
                }}
              />
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            CSV columns:{" "}
            <code>
              {allowDoorSign
                ? "serial, room, model, role"
                : "serial, room, model"}
            </code>{" "}
            (header optional; room matches a room number/name or id
            {allowDoorSign ? (
              <>
                ; role is <code>tv</code> or <code>door-sign</code>, default{" "}
                <code>tv</code>
              </>
            ) : null}
            ).
          </Typography>

          {lastResult && (
            <Alert
              severity={lastResult.errors.length > 0 ? "warning" : "success"}
              onClose={() => setLastResult(null)}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {lastResult.created.length} device
                {lastResult.created.length === 1 ? "" : "s"} provisioned
                {lastResult.errors.length > 0
                  ? ` · ${lastResult.errors.length} skipped`
                  : ""}
              </Typography>
              {lastResult.errors.length > 0 && (
                <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                  {lastResult.errors.slice(0, 12).map((e, i) => (
                    <li key={i}>
                      <Typography variant="caption">
                        {e.row ? `Row ${e.row}: ` : ""}
                        {e.serial ? `${e.serial} - ` : ""}
                        {reasonLabel(e.reason)}
                      </Typography>
                    </li>
                  ))}
                  {lastResult.errors.length > 12 && (
                    <li>
                      <Typography variant="caption">
                        +{lastResult.errors.length - 12} more…
                      </Typography>
                    </li>
                  )}
                </Box>
              )}
            </Alert>
          )}

          {/* Awaiting-activation list */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Awaiting activation
            </Typography>
            {awaiting.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No pre-seeded devices waiting. Add one above or import a CSV.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Room</TableCell>
                    {allowDoorSign && <TableCell>Role</TableCell>}
                    <TableCell>Serial</TableCell>
                    <TableCell>Model</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {awaiting.map((s) => (
                    <TableRow key={s.id} hover>
                      <TableCell>{roomLabel(s.roomId)}</TableCell>
                      {allowDoorSign && (
                        <TableCell>
                          <Chip
                            size="small"
                            label={roleLabel(s.role)}
                            color={
                              s.role === "door-sign" ? "secondary" : "default"
                            }
                            variant={
                              s.role === "door-sign" ? "filled" : "outlined"
                            }
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: "monospace" }}
                        >
                          {s.serial || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>{s.model || "-"}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          color="error"
                          startIcon={<DeleteOutlineIcon />}
                          onClick={() => void removeOne(s.id)}
                        >
                          Remove
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
